import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TablePagination,
  IconButton,
  Tooltip,
  Grid2,
  Button,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataTableStyles } from ".";
import Dropdown from "../Dropdown/Dropdown";
import { ROLE_RULES } from "../../Utils/roleChangeRules";
import { useSelector } from "react-redux";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useLocation } from "react-router-dom";

// Header Component
export const Header = ({
  titleOptions,
  button,
  buttonFun,
  buttonDisabled = false,
  bulk,
  bulkFun,
  count = false,
  header = false,
  activeTitle,
  handleTitleClick,
  onClick,
}) => {
  return (
    <>
      {header ? (
        <Grid2 className={DataTableStyles.headerContainer}>
          <Grid2 className={DataTableStyles.FlexBox}>
            <Grid2 className={DataTableStyles.title}>
              {titleOptions.map((option, index) => (
                <Grid2
                  key={index}
                  container
                  alignItems="center"
                  style={{
                    cursor:
                      activeTitle?.option?.length > 1 ? "pointer" : "default",
                  }}
                  className={`
                    ${DataTableStyles.titleOption}
                    ${
                      activeTitle !== option.label
                        ? DataTableStyles.unselectedOption
                        : ""
                    }
                  `}
                  onClick={() => handleTitleClick(option.label)}
                >
                  <Grid2
                    className={`
                      ${DataTableStyles.label}
                      ${
                        titleOptions.length > 1 && activeTitle === option.label
                          ? DataTableStyles.activeTitleOption
                          : ""
                      }
                    `}
                    onClick={option.onClick}
                  >
                    {option.label}
                    {option.count ? (
                      <Grid2
                        className={`${DataTableStyles.optionColor}`}
                        style={{ backgroundColor: option.color }}
                      >
                        {option.count}
                      </Grid2>
                    ) : null}
                    {option.icon ? <Grid2>{option.icon}</Grid2> : null}
                  </Grid2>
                </Grid2>
              ))}
            </Grid2>
            <Grid2>
              <Button className={DataTableStyles.button} onClick={bulkFun}>
                {bulk}
              </Button>
              <Button className={DataTableStyles.button} onClick={buttonFun} disabled={buttonDisabled}>
                {button}
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      ) : null}
    </>
  );
};

// DataTable Component
export const DataTable = (props) => {
  const {
    columns = [],
    rows = [],
    icon = false,
    enableSelection = false,
    enablePagination = false,
    enableTeamDelete = false,
    headerProps = {},
    statusConfig = {},
    isStatusColumn = () => false,
    dropdownHeader,
    onDropdownSelect,
    dropdownList,
    memberRole,
    onClick,
    onSelectedClick,
  } = props;

  const location = useLocation();
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [activeTitle, setActiveTitle] = useState(
    headerProps.titleOptions[0]?.label || headerProps.activeFilter
  );
  let userDetails = useSelector((state) => state.users.userDetails?.theme);
  const teamMembers = location?.pathname.includes("/team-members");
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(rows.map((row) => row.name));
    } else {
      setSelected([]);
    }
  };

  const handleSelectClick = (event, id) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // update page if rows are deleted
  useEffect(() => {
    if (page > 0 && rows.length <= page * rowsPerPage) {
      setPage(Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1));
    }
  }, [rows, rowsPerPage, page]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTitleClick = (titleLabel) => {
    setActiveTitle(titleLabel);
    headerProps.titleOptions
      .find((option) => option.label === titleLabel)
      ?.onClick();
  };

  const hasSelectedItems = selected.length > 0;

  return (
    <>
      <Grid2 className={DataTableStyles.DataTableContainer}>
        <Header
          titleOptions={headerProps.titleOptions}
          button={headerProps.button}
          bulk={headerProps.bulk}
          header={headerProps.header}
          activeTitle={activeTitle}
          handleTitleClick={handleTitleClick}
          buttonFun={headerProps.buttonFun}
          buttonDisabled={headerProps.buttonDisabled}
          bulkFun={headerProps.bulkFun}
        />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                className={`${
                  !hasSelectedItems
                    ? DataTableStyles.TableRowHead
                    : DataTableStyles.deleteRowHead
                }`}
              >
                {enableSelection && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      sx={{ ml: 2 }}
                      indeterminate={
                        selected.length > 0 && selected.length < rows.length
                      }
                      checked={
                        rows.length > 0 && selected.length === rows.length
                      }
                      onChange={handleSelectAllClick}
                      inputProps={{
                        "aria-label": "select all rows",
                      }}
                    />
                  </TableCell>
                )}
                {columns.map((column, index) => (
                  <TableCell
                    key={index}
                    className={DataTableStyles.tableCellColor}
                  >
                    {!hasSelectedItems && column.tooltip ? (
                      <Grid2 className={DataTableStyles.tooltipCenter}>
                        {column.label}{" "}
                        <Tooltip
                          title={column.tooltip}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (column.tooltipOnClick) {
                              column.tooltipOnClick();
                            }
                          }}
                        >
                          <InfoOutlinedIcon
                            className={DataTableStyles.infoIcon}
                          />
                        </Tooltip>
                      </Grid2>
                    ) : (
                      <>
                        {!hasSelectedItems && (
                          <Grid2 className={DataTableStyles.TableHeadText}>
                            {icon ? <>{column.icon}</> : null}
                            {column.label}
                          </Grid2>
                        )}
                      </>
                    )}
                  </TableCell>
                ))}
                {enableTeamDelete && (
                  <TableCell className={DataTableStyles.tableCellColor}>
                    {!hasSelectedItems ? (
                      <Grid2 className={DataTableStyles.TableHeadText}>
                        Delete
                      </Grid2>
                    ) : (
                      <Grid2
                        className={DataTableStyles.button}
                        onClick={onSelectedClick}
                      >
                        <DeleteOutlinedIcon
                          className={DataTableStyles.deleteIconColor}
                        />
                      </Grid2>
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => {
                  const isItemSelected = enableSelection
                    ? isSelected(row.name)
                    : false;
                  return (
                    <TableRow
                      key={rowIndex}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      {enableSelection && (
                        <TableCell
                          padding="checkbox"
                          className={DataTableStyles.TableCellBorder}
                        >
                          <Checkbox
                            sx={{ ml: 2 }}
                            checked={isItemSelected}
                            onClick={(event) =>
                              handleSelectClick(event, row.name)
                            }
                            inputProps={{
                              "aria-labelledby": `enhanced-table-checkbox-${rowIndex}`,
                            }}
                          />
                        </TableCell>
                      )}
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={colIndex}
                          align="left"
                          className={DataTableStyles.tableCellColor}
                        >
                          {statusConfig &&
                          isStatusColumn &&
                          isStatusColumn(column) ? (
                            <div className={DataTableStyles.statusContainer}>
                              <div
                                className={DataTableStyles.iconContainer}
                                style={{
                                  color:
                                    statusConfig &&
                                    isStatusColumn &&
                                    isStatusColumn(column) &&
                                    row[column.id]?.toLowerCase() &&
                                    statusConfig[row[column.id]?.toLowerCase()]
                                      ?.color
                                      ? statusConfig[
                                          row[column.id]?.toLowerCase()
                                        ].color
                                      : "#0b0d0e",
                                }}
                              >
                                {
                                  statusConfig[row[column.id]?.toLowerCase()]
                                    ?.icon
                                }
                              </div>
                              <span className={DataTableStyles.statusText}>
                                {
                                  statusConfig[row[column.id]?.toLowerCase()]
                                    ?.label
                                }
                              </span>
                            </div>
                          ) : (
                            !column.hasDropdown && row[column.id]
                          )}

                          {!isStatusColumn(column) && column.hasDropdown && (
                            <>
                              {!column.hasDropdown ||
                              !ROLE_RULES[memberRole]?.includes(row.role) ? (
                                <Grid2
                                  className={
                                    DataTableStyles.noDropdownContainer
                                  }
                                >
                                  {row[column.id]}
                                </Grid2>
                              ) : (
                                <Grid2 className={DataTableStyles.dropdownLoad}>
                                  {row.dropdownLoad ? (
                                    <CircularProgress
                                      size={20}
                                      className={DataTableStyles.dropload}
                                    />
                                  ) : (
                                    <Dropdown
                                      key={rowIndex}
                                      placeholder={row[column.id]}
                                      options={column.dropdownOptions}
                                      onSelect={(selectedValue) => {
                                        onDropdownSelect(
                                          selectedValue,
                                          rowIndex,
                                          page,
                                          rowsPerPage
                                        );
                                      }}
                                      label={column.label}
                                      dropdownHeader={dropdownHeader}
                                      dropdownList={dropdownList}
                                    />
                                  )}
                                </Grid2>
                              )}
                            </>
                          )}
                        </TableCell>
                      ))}
                      {/* This is for Team Members */}
                      {enableTeamDelete &&
                      ROLE_RULES[memberRole]?.includes(row.role) ? (
                        <TableCell className={DataTableStyles.TableCellBorder}>
                          <IconButton
                            aria-label="delete"
                            className={
                              hasSelectedItems
                                ? DataTableStyles.disableIcon
                                : DataTableStyles.IconButton
                            }
                            onClick={() =>
                              !row.loading &&
                              onClick(rowIndex, page, rowsPerPage)
                            }
                          >
                            {row.loading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteOutlinedIcon
                                className={DataTableStyles.deleteIconColor}
                              />
                            )}
                          </IconButton>
                        </TableCell>
                      ) : teamMembers ? (
                        <TableCell
                          className={DataTableStyles.TableCellBorder}
                        />
                      ) : (
                        ""
                      )}
                    </TableRow>
                  );
                })}
              {rows?.length < 5 &&
                Array.from(new Array(5 - rows.length)).map((_, index) => (
                  <TableRow key={`empty-row-${index}`} style={{ height: 53 }}>
                    {""}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {enablePagination && (
            <TablePagination
              sx={{
                fontFamily: "'Nunito', sans-serif",
                "& .MuiTablePagination-selectLabel": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                },
                "& .MuiTablePagination-displayedRows": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiTablePagination-actions": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiTablePagination-input": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiSelect-icon": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiButtonBase-root.Mui-disabled": {
                  color: userDetails === "dark" ? "#ffffff" : "#000000",
                  opacity: "0.5",
                  fontFamily: "'Nunito', sans-serif",
                },
                "& .MuiTablePagination-select": {
                  fontFamily: "'Nunito', sans-serif",
                },
              }}
              rowsPerPageOptions={[5, 10, 15]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </TableContainer>
      </Grid2>
    </>
  );
};

export default DataTable;
